import { Dispatch, FC, SetStateAction } from "react";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { useFormContext } from "react-hook-form";
import { BeefIntervention } from "../../../../models/Intervention/BeefIntervention";
import { DsmGrid, DsmIcon } from "@dsm-dcs/design-system-react";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import { Spacer } from "../../../helpers/Spacer";
import { FormType, ListEntry } from "../../common";
import RowTextFieldWithMetrics from "../../../../../modules/Farms/Intervention/RowControlledTextFieldWithMetrics2";
import { StageType } from "../../../../../../graphql/types";
import { capitalize } from "lodash";
import ReactHookDsmInput from "../../../../../modules/Helpers/ReactHookDsmInput2";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { unitLong } from "../../../../utils/unit-utils";
import EditModeButtons from "../../../helpers/EditModeButtons";
import { BeefBaseline, BeefInputBreeding, BeefInputGrowing } from "../../../../models/Baseline/BeefBaseline";
import { InternalSource } from "../../../../models/Baseline/CattleBaseline";
import ReactHookDsmRadioGroup from "../../../../../modules/Helpers/ReactHookDsmRadioGroup";
import { Tooltip } from "@material-ui/core";
import { AnimalType } from "../../../../../../graphql/generated/blonk/cattle";
import { isFieldFilled } from "../CommonDataParts/InterventionGenericInfoDialog";

interface BeefInputIntSourceComponentProps {
  formType: FormType;
  stageType: StageType;
  fieldItemPrefix: string;
  productionProcessName: string;
  defaultStageData: BeefInputGrowing | BeefInputBreeding;
  isInternalEdit: boolean;
  setIsInternalEdit: Dispatch<SetStateAction<boolean>>;
  intSourceCancelHandler: (isInnerCancel?: boolean) => void;
  baseline: BeefBaseline;
}

const BeefInputIntSourceComponent: FC<BeefInputIntSourceComponentProps> = ({
  formType,
  stageType,
  fieldItemPrefix,
  productionProcessName,
  defaultStageData,
  isInternalEdit,
  setIsInternalEdit,
  intSourceCancelHandler,
  baseline,
}) => {
  const intl = useIntl();
  const classes: CSSClassesList = processAndStageStylesV2();
  const fc = useFormContext<BeefIntervention>();
  const userProfile = UserProfilePrefs.getInstance();
  const userUOM = userProfile.getUserUnitPrefs();
  const distanceUnit = unitLong(
    userUOM.unitTransportDistanceTerrestrial as string
  );

  const animalSystemTypeOptions = [{ text: "Beef", value: "BEEF" }] as ListEntry[];
  const formatStageType = (stageType: string): string => capitalize(stageType.toLowerCase().replace('_', ' '));

  const defaultFarm = (index: number): ListEntry[] => {
    const defaultFarm = (defaultStageData.internalSources ?? [])[index];
    return [{
      text: defaultFarm.farmName,
      value: defaultFarm.farmId
    }]
  };

  const defaultStage = (index: number): ListEntry[] => {
    const defaultStage = (defaultStageData.internalSources ?? [])[index];
    const stageId = defaultStage.originStageId;
    const originalStage = baseline.stages.find(stage => stage.id === stageId);
    const defaultStageName = originalStage !== undefined
      ? originalStage.name
      : defaultStage.stageName;

    return [{
      text: `${productionProcessName} - ${defaultStageName} (${formatStageType(defaultStage.stageType)})`,
      value: defaultStage.originStageId
    }]
  }

  const animalTypeOptions = () =>
    Object.entries(AnimalType).map(([label, value]) => ({
      label,
      value,
    }));

  const internalSourceUnitStyle = (defaultStageData?.internalSources?.length ?? 0) > 1
    ? classes.additionalEntriesBlockHolder
    : classes.additionalEntriesBlockHolderWithoutBorder;

  const internalSourceUnit = (intSource: InternalSource, index: number) => {
    const fieldPrefix = `${fieldItemPrefix}.internalSources[${index}]`;
    const selectedStageType = intSource.stageType;
    const selectedAnimalType = intSource.animalType;
    const defFarm = defaultFarm(index);
    const defStage = defaultStage(index);

    return (
      <DsmGrid className={internalSourceUnitStyle}>
        <Spacer />
        {stageType === StageType.Growing &&
          <>
            <ReactHookDsmSelect
              style={{ width: '100%' }}
              name={`${fieldItemPrefix}.animalSystemType`}
              label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.ANIMAL_SYSTEM_TYPE' })}
              tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.ANIMAL_SYSTEM_TYPE.TOOLTIP' })}
              defaultValue={intSource.animalSystemType}
              options={animalSystemTypeOptions}
              disabled
            />
            <Spacer height="16px" />
          </>}
        <ReactHookDsmSelect
          style={{ width: '100%' }}
          name={`${fieldPrefix}.farmId`}
          label={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.FARM' })}
          adornment={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.FARM.ADORNMENT' })}
          tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.FARM.TOOLTIP' })}
          defaultValue={defFarm[0]?.value}
          options={defFarm}
          disabled
        />
        <Spacer height="16px" />
        <ReactHookDsmSelect
          style={{ width: '100%' }}
          name={`${fieldPrefix}.originStageId`}
          label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.STAGE` })}
          adornment={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.STAGE.ADORNMENT` })}
          tooltip={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.STAGE.TOOLTIP' })}
          options={defStage}
          defaultValue={defStage[0]?.value}
          disabled
        />
        <Spacer height="16px" />
        {stageType === StageType.Growing && selectedStageType === StageType.Breeding && (
          <DsmGrid className={classes.dsmGridSmallerColumnNoRowSpacing}>
            <ReactHookDsmRadioGroup
              name={`${fieldItemPrefix}.animalType`}
              label={`${intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.ANIMAL_TYPE' })}`}
              defaultValue={selectedAnimalType}
              options={animalTypeOptions()}
              disabled
            />
            <Tooltip
              style={{ transform: 'translateY(85%)' }}
              title={intl.formatMessage({ id: 'SUSTELL.STAGE.BEEF.INPUT.ANIMAL_TYPE.TOOLTIP' })}
              placement="left"
            >
              <DsmIcon
                name="general/help-circle"
                className={classes.inlineTooltipClass}
                title=""
              />
            </Tooltip>
          </DsmGrid>
        )}
        {!isInternalEdit ? (
          <>
            {isFieldFilled(fc.getValues(`${fieldPrefix}.numberAnimals`)) ? (
              <>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.numberAnimals`}
                  label={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.ANIMALS_ENTERING_STAGE` })}
                  tooltip={intl.formatMessage({
                    id: `SUSTELL.STAGE.BEEF.INPUT.${stageType}.ANIMALS_ENTERING_STAGE.TOOLTIP`
                  })}
                  type="number"
                  metricUnit={intl.formatMessage({
                    id: `SUSTELL.STAGE.BEEF.INPUT.ANIMALS_ENTERING_STAGE.ADORNMENT`
                  }).toLowerCase()}
                  baseline={intSource.numberAnimals}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalNumberAnimals`}
                label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE` })}
                adornment={intl.formatMessage({
                  id: `SUSTELL.STAGE.BEEF.INPUT.ANIMALS_ENTERING_STAGE.ADORNMENT`
                }).toLowerCase()}
                tooltip={intl.formatMessage({
                  id: `SUSTELL.STAGE.BEEF.INPUT.${stageType}.ANIMALS_ENTERING_STAGE.TOOLTIP`
                })}
                type="number"
                defaultValue={intSource.numberAnimals ?? ''}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.distanceTruck`)) ? (
              <>
                <RowTextFieldWithMetrics
                  name={`${fieldPrefix}.distanceTruck`}
                  label={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.DISTANCE_TRUCK` })}
                  tooltip={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.${stageType}.DISTANCE_TRUCK.TOOLTIP` })}
                  type="number"
                  metricUnit={distanceUnit}
                  baseline={intSource.distanceTruck}
                  displayTypeSelect={false}
                  disabled
                />
                <Spacer />
              </>
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalDistanceTruck`}
                label={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.DISTANCE_TRUCK` })}
                adornment={distanceUnit}
                tooltip={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.${stageType}.DISTANCE_TRUCK.TOOLTIP` })}
                type="number"
                defaultValue={intSource.distanceTruck ?? ''}
                disabled
              />
            )}
            {isFieldFilled(fc.getValues(`${fieldPrefix}.shrinkingRate`)) ? (
              <RowTextFieldWithMetrics
                name={`${fieldPrefix}.shrinkingRate`}
                label={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.WEIGHT_LOSS` })}
                tooltip={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.WEIGHT_LOSS.TOOLTIP` })}
                type="number"
                metricUnit="%"
                baseline={intSource.shrinkingRate}
                displayTypeSelect={false}
                disabled
              />
            ) : (
              <ReactHookDsmInput
                name={`${fieldPrefix}.originalShrinkingRate`}
                label={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.WEIGHT_LOSS` })}
                tooltip={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.WEIGHT_LOSS.TOOLTIP` })}
                placeholder={intl.formatMessage({ id: `SUSTELL.STAGE.BEEF.INPUT.WEIGHT_LOSS.PLACEHOLDER` })}
                adornment="%"
                type="number"
                defaultValue={intSource.shrinkingRate ?? ''}
                disabled
              />
            )}
          </>
        ) : (
          <>
            <RowTextFieldWithMetrics
              name={`${fieldPrefix}.numberAnimals`}
              label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE` })}
              tooltip={intl.formatMessage({
                id: stageType === StageType.ContinuousGrowing
                  ? `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.CONTINUOUS_GROWING.TOOLTIP`
                  : `SUSTELL.STAGE.DAIRY.INPUT.ANIMALS_ENTERING_STAGE.TOOLTIP`,
              })}
              type="number"
              metricUnit={intl.formatMessage({
                id: `SUSTELL.STAGE.BEEF.INPUT.ANIMALS_ENTERING_STAGE.ADORNMENT`
              }).toLowerCase()}
              baseline={intSource.numberAnimals}
              disabled={false}
            />
            <Spacer />
            <RowTextFieldWithMetrics
              name={`${fieldPrefix}.distanceTruck`}
              label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK` })}
              tooltip={intl.formatMessage({
                id: stageType === StageType.ContinuousGrowing
                  ? `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.CONTINUOUS_GROWING.TOOLTIP`
                  : `SUSTELL.STAGE.DAIRY.INPUT.DISTANCE_TRUCK.TOOLTIP`,
              })}
              type="number"
              metricUnit={distanceUnit}
              baseline={intSource.distanceTruck}
              disabled={false}
            />
            <Spacer />
            <RowTextFieldWithMetrics
              name={`${fieldPrefix}.shrinkingRate`}
              label={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS` })}
              tooltip={intl.formatMessage({ id: `SUSTELL.STAGE.DAIRY.INPUT.WEIGHT_LOSS.TOOLTIP` })}
              type="number"
              metricUnit="%"
              baseline={intSource.shrinkingRate}
              disabled={false}
            />
          </>
        )}
      </DsmGrid>
    )
  }

  return (
    <div>
      {!!defaultStageData?.internalSources?.length &&
        <DsmGrid className={classes.dsmGridOneColumnInterventionWithBorder}>
          {(defaultStageData?.internalSources || [])
            ?.filter((item) => !!item.farmId)
            ?.map((item, index) => (internalSourceUnit(item, index)))}
          {formType !== FormType.View &&
            <EditModeButtons
              isEditing={isInternalEdit}
              onEdit={() => setIsInternalEdit(true)}
              onCancel={intSourceCancelHandler}
            />}
        </DsmGrid>}
    </div>
  )
}

export default BeefInputIntSourceComponent;
